import { Controller } from "@hotwired/stimulus"

// Cell background = RGB average of these, weighted by source count per
// product. Unknown products are ignored; cells with none fall back to violet.
const PRODUCT_RGB = {
  edr:  { light: [96, 165, 250],  dark: [29, 78, 216] },  // blue-400 / blue-700
  itdr: { light: [248, 113, 113], dark: [185, 28, 28] },  // red-400 / red-700
  siem: { light: [74, 222, 128],  dark: [21, 128, 61] },  // green-400 / green-700
}
const UNKNOWN_PRODUCT_RGB = { light: [167, 139, 250], dark: [109, 40, 217] } // violet-400 / violet-700

// Type pill colors [bg, border, text], mirroring the Fields tab's UI::Pill
// palette. Dark mode swaps bg↔text.
const TYPE_PALETTE = {
  string:  ["#dbeafe", "#60a5fa", "#1e3a8a"], // blue
  integer: ["#dcfce7", "#4ade80", "#14532d"], // green
  number:  ["#dcfce7", "#4ade80", "#14532d"],
  boolean: ["#fef9c3", "#facc15", "#713f12"], // yellow
  array:   ["#fee2e2", "#f87171", "#7f1d1d"], // red
}
const DEFAULT_TYPE_PALETTE = ["#f1f5f9", "#94a3b8", "#0f172a"] // slate

const PILL_BASE = "inline-flex items-center rounded-full px-1.5 text-[10px] leading-4 whitespace-nowrap"
const NULLABLE_PILL_CLASSES = `${PILL_BASE} font-medium bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-300 border border-gray-300 dark:border-gray-600`

export default class extends Controller {
  static values = { dataId: String, tabHref: String, modalId: String }

  connect() {
    this.items = this._readData()
    this.selected = new Set()
    this._onResize = () => this._renderIfReady()
    this._resizeObserver = new ResizeObserver(this._onResize)
    this._resizeObserver.observe(this.element)

    // First render waits for the tab to be visible (clientWidth is 0 while
    // display:none). ResizeObserver handles it; shown.bs.tab is a belt-and-
    // suspenders trigger for Bootstrap tabs.
    this._jq = window.jQuery
    if (this._jq) {
      this._onTabShown = (e) => {
        if (e.target?.getAttribute?.("href") === this.tabHrefValue) this._renderIfReady()
      }
      this._jq(document).on("shown.bs.tab", this._onTabShown)
    }

    this._shiftHeld = false
    this._hoverTimer = null
    this._searchQuery = ""
    this._searchDebounce = null
    this._tooltip = document.getElementById("event-shared-treemap-tooltip")
    this._bindings = []

    // External elements (siblings of this.element) that Stimulus actions
    // can't reach — bind manually; _bind records listeners for disconnect.
    this._bind(document, "keydown", (e) => {
      if (e.key === "Escape" && this.selected.size > 0) this._clearSelection()
      if (e.shiftKey) { this._shiftHeld = true; this._cancelHoverTimer(); this._hideTooltip() }
    })
    this._bind(document, "keyup", (e) => { if (!e.shiftKey) this._shiftHeld = false })
    this._fab = this._bind("event-shared-intersection-fab", "click", () => this.openIntersection())
    this._fabCount = document.getElementById("event-shared-intersection-fab-count")
    this._clearBtn = this._bind("event-shared-clear-selection-btn", "click", () => this._clearSelection())
    this._searchInput = this._bind("event-shared-treemap-search", "input", () => this._scheduleSearch())

    // Re-render on dark-mode toggle so cell colors, legend, and contrast
    // match the active theme. Selection and hover state are rebuilt by _render.
    this._bind(window, "dark-mode-changed", () => { this._renderLegend(); this._renderIfReady() })

    this._renderLegend()
    this._renderIfReady()
  }

  disconnect() {
    this._resizeObserver?.disconnect()
    if (this._jq && this._onTabShown) this._jq(document).off("shown.bs.tab", this._onTabShown)
    this._bindings.forEach(({ el, event, handler }) => el.removeEventListener(event, handler))
    if (this._searchDebounce) { clearTimeout(this._searchDebounce); this._searchDebounce = null }
    this._cancelHoverTimer()
  }

  // Register a listener and record it for disconnect. target may be an
  // element, document, or an element id. Returns the resolved element (or null).
  _bind(target, event, handler) {
    const el = typeof target === "string" ? document.getElementById(target) : target
    if (!el) return null
    el.addEventListener(event, handler)
    this._bindings.push({ el, event, handler })
    return el
  }

  onCellClick(e) {
    if (e.shiftKey) {
      e.preventDefault()
      this._toggleSelection(e.currentTarget)
      return
    }
    this.showSources(e)
  }

  // Enter/Space on a focused cell: Shift toggles selection, otherwise opens sources.
  onCellKeyDown(e) {
    if (e.key !== "Enter" && e.key !== " ") return
    e.preventDefault()
    if (e.shiftKey) this._toggleSelection(e.currentTarget)
    else this.showSources(e)
  }

  onContainerClick(e) {
    // Only the gap between cells has e.target === this.element (cells bubble).
    if (e.target === this.element && this.selected.size > 0) this._clearSelection()
  }

  onCellMouseEnter(e) {
    if (this._shiftHeld) return
    const cell = e.currentTarget
    this._cancelHoverTimer()
    this._hoverTimer = setTimeout(() => {
      if (this._shiftHeld) return
      this._showTooltip(cell, e)
    }, 500)
  }

  onCellMouseLeave() {
    this._cancelHoverTimer()
    this._hideTooltip()
  }

  _cancelHoverTimer() {
    if (this._hoverTimer) { clearTimeout(this._hoverTimer); this._hoverTimer = null }
  }

  _showTooltip(cell, mouseEvent) {
    if (!this._tooltip) return
    const item = this._cellItem(cell)
    if (!item) return

    const typeLabel = this._typeLabel(item.type, " | ")
    const { breakdown } = this._mixProductColor(item.sources, this._isDark())

    this._tooltip.innerHTML = `
      <div class="font-mono font-semibold mb-0.5">${this._esc(item.name)}</div>
      <div class="opacity-80 mb-1">type: ${this._esc(typeLabel)} • shared by ${this._countOf(item.overlap, "event type")}</div>
      ${breakdown ? `<div class="opacity-80">${this._esc(breakdown)}</div>` : ""}
      <div class="opacity-60 mt-1 italic">click for sources · shift+click to select</div>
    `

    // Position near the cursor within the treemap wrapper, flipping to the
    // opposite side if the tooltip would spill out of view.
    const host = this.element.parentElement || this.element
    const hostRect = host.getBoundingClientRect()
    this._tooltip.classList.remove("hidden")
    const ttRect = this._tooltip.getBoundingClientRect()
    const pad = 12
    const relX = mouseEvent.clientX - hostRect.left
    const relY = mouseEvent.clientY - hostRect.top
    const x = relX + ttRect.width + pad > hostRect.width ? relX - ttRect.width - pad : relX + pad
    const y = relY + ttRect.height + pad > hostRect.height ? relY - ttRect.height - pad : relY + pad
    this._tooltip.style.left = `${Math.max(0, x)}px`
    this._tooltip.style.top = `${Math.max(0, y)}px`
  }

  _hideTooltip() {
    if (this._tooltip) this._tooltip.classList.add("hidden")
  }

  // Generic helpers used throughout.
  // The app's dark mode toggles a `dark` class on document.body (see
  // controllers/components/dark_mode_controller.js).
  _isDark() { return document.body.classList.contains("dark") }
  _cellItem(cellEl) { return this.items[Number(cellEl.dataset.index)] }
  _typeLabel(type, sep = "|") { return Array.isArray(type) ? type.join(sep) : String(type ?? "") }
  _plural(n, singular) { return n === 1 ? singular : `${singular}s` }
  _countOf(n, singular) { return `${n} ${this._plural(n, singular)}` }
  _openModal(id) { document.dispatchEvent(new CustomEvent("ui:dialog:open", { detail: { id } })) }

  showSources(e) {
    e.preventDefault()
    const item = this._cellItem(e.currentTarget)
    if (!item) return

    const modalId = this.modalIdValue
    const part = suffix => document.getElementById(`${modalId}${suffix}`)

    const badge = part("-badge")
    if (badge) badge.textContent = `${this._typeLabel(item.type)} • ${this._countOf(item.overlap, "source")}`
    const desc = part("-desc")
    if (desc) desc.textContent = item.name
    const h3 = part("")?.querySelector("h3")
    if (h3) h3.textContent = item.name
    const tbody = part("-body")
    if (tbody) {
      const nullable = this._isNullable(item.type)
      tbody.innerHTML = item.sources.map(s => this._sourceRow(s, nullable)).join("")
    }

    this._openModal(modalId)
  }

  openIntersection() {
    const rows = this._computeIntersection()
    const cells = [...this.selected].map(i => this.items[i]).filter(Boolean)
    const selCount = cells.length
    const part = suffix => document.getElementById(`event-shared-intersection-modal${suffix}`)

    const badge = part("-badge")
    if (badge) badge.textContent = `${this._countOf(selCount, "field")} selected • ${this._countOf(rows.length, "event type")}`

    const desc = part("-desc")
    if (desc) desc.innerHTML = this._buildIntersectionDescription(cells, rows.length)

    const tbody = part("-body")
    if (tbody) {
      tbody.innerHTML = rows.length === 0
        ? '<tr><td colspan="3" class="py-6 px-3 text-center text-sm text-semantics-text-secondary dark:text-semantics-text-secondary-dark">No event types are shared across all selected fields.</td></tr>'
        : rows.map(s => this._sourceRow(s)).join("")
    }

    // Footer hint — only shown when there are shared event types.
    const footer = part("-footer")
    if (footer) {
      footer.style.display = rows.length === 0 ? "none" : ""
      if (rows.length > 0) {
        const subject = selCount === 1 ? "this field" : `these ${selCount} fields`
        footer.textContent = `You can build a detection rule that uses ${subject} and will work against all the above telemetry types.`
      }
    }

    this._openModal("event-shared-intersection-modal")
  }

  _buildIntersectionDescription(cells, rowCount) {
    if (cells.length === 0) return ""
    const sorted = [...cells].sort((a, b) => (a.name || "").localeCompare(b.name || ""))
    const chipHtml = c =>
      `<span class="font-mono text-semantics-text-primary dark:text-semantics-text-primary-dark">${this._esc(c.name)}</span>`
    const chips = sorted.map(chipHtml)
    // Natural-language join: "A", "A and B", or "A, B, and C".
    let joined
    if (chips.length < 2) joined = chips[0]
    else if (chips.length === 2) joined = `${chips[0]} and ${chips[1]}`
    else joined = `${chips.slice(0, -1).join(", ")}, and ${chips.at(-1)}`

    const solo = cells.length === 1
    const lead = `You have selected ${this._countOf(cells.length, "field")}: ${joined}.`
    const ets = this._plural(rowCount, "event type")
    const tail = rowCount === 0
      ? (solo ? "This field does not appear in any event types." : "These fields are not common across any event types.")
      : (solo ? `This field is present in the following ${ets}:` : `These fields are common across the following ${ets}:`)
    return `${lead} ${tail}`
  }

  _toggleSelection(cellEl) {
    const idx = Number(cellEl.dataset.index)
    if (Number.isNaN(idx)) return
    const nowSelected = !this.selected.has(idx)
    if (nowSelected) this.selected.add(idx); else this.selected.delete(idx)
    this._setSelected(cellEl, nowSelected)
    this._refreshFab()
  }

  _clearSelection() {
    this.selected.clear()
    this.element.querySelectorAll("[data-index]").forEach(el => this._setSelected(el, false))
    this._refreshFab()
  }

  // Inline styles avoid Tailwind JIT misses on runtime-added classes.
  _setSelected(el, on) {
    el.style.outline = on ? "2px solid #2563eb" : ""
    el.style.outlineOffset = on ? "-2px" : ""
    el.setAttribute("aria-pressed", on ? "true" : "false")
  }

  _refreshFab() {
    if (this._fab && this._fabCount) {
      this._fabCount.textContent = String(this.selected.size)
      this._fab.style.display = this.selected.size === 0 ? "none" : ""
    }
    if (this._clearBtn) {
      this._clearBtn.style.display = this.selected.size >= 2 ? "" : "none"
    }
  }

  _computeIntersection() {
    const cells = [...this.selected].map(i => this.items[i]).filter(Boolean)
    if (cells.length === 0) return []
    // Compound key: same event_type can repeat under different product/capability.
    const key = s => `${s.event_type}||${s.product || ""}||${s.capability || ""}`
    // Keep cells[0] sources present in every other cell. Single-cell: empty
    // otherSets, all pass.
    const otherSets = cells.slice(1).map(c => new Set(c.sources.map(key)))
    const seen = new Set()
    const result = []
    for (const s of cells[0].sources) {
      const k = key(s)
      if (seen.has(k) || !otherSets.every(set => set.has(k))) continue
      seen.add(k); result.push(s)
    }
    const ci = (a, b) => (a || "").localeCompare(b || "", undefined, { sensitivity: "base" })
    return result.sort((a, b) =>
      ci(a.event_type, b.event_type) || ci(a.product, b.product) || ci(a.capability, b.capability)
    )
  }

  _sourceRow(s, nullable = false) {
    const nullableBadge = nullable
      ? ` <span class="${NULLABLE_PILL_CLASSES} align-middle ml-2">nullable</span>`
      : ""
    return `
      <tr>
        <td class="py-2 px-3 text-sm text-semantics-text-primary dark:text-semantics-text-primary-dark">${this._esc(s.event_type)}${nullableBadge}</td>
        <td class="py-2 px-3 text-sm text-semantics-text-primary dark:text-semantics-text-primary-dark font-mono">${this._esc((s.product || "").toUpperCase())}</td>
        <td class="py-2 px-3 text-sm text-semantics-text-secondary dark:text-semantics-text-secondary-dark">${this._esc(s.capability || "—")}</td>
      </tr>`
  }

  _readData() {
    const node = document.getElementById(this.dataIdValue)
    try { return JSON.parse(node?.textContent || "[]") } catch { return [] }
  }

  _renderIfReady() {
    if (this.element.clientWidth > 0 && this.element.clientHeight > 0) this._render()
  }

  _render() {
    const w = this.element.clientWidth
    const h = this.element.clientHeight
    if (w === 0 || h === 0 || this.items.length === 0) return

    // Reset interaction state — the old cells and their outlines are about to
    // be wiped, and preserving selection across a resize is more confusing
    // than starting fresh.
    if (this.selected.size > 0) {
      this.selected.clear()
      this._refreshFab()
    }
    this._cancelHoverTimer()
    this._hideTooltip()

    const values = this.items.map(i => Math.max(0.0001, i.overlap))
    const totalArea = values.reduce((a, b) => a + b, 0)
    const scale = (w * h) / totalArea
    const scaled = values.map(v => v * scale)

    const rects = squarify(scaled, { x: 0, y: 0, w, h })
    this.element.innerHTML = ""
    rects.forEach((r, i) => this.element.appendChild(this._cell(this.items[i], r, i)))

    // Re-apply active search so fresh cells inherit the fade state.
    if (this._searchQuery) this._applySearch()
  }

  _cell(item, rect, index) {
    const isDark = this._isDark()
    const { bg, fg } = this._mixProductColor(item.sources, isDark)

    const cell = document.createElement("div")
    cell.className = "absolute overflow-hidden cursor-pointer flex flex-col justify-start leading-none transition-opacity hover:opacity-80"
    cell.style.left = `${rect.x}px`
    cell.style.top = `${rect.y}px`
    cell.style.width = `${Math.max(0, rect.w - 1)}px`
    cell.style.height = `${Math.max(0, rect.h - 1)}px`
    cell.style.background = bg
    cell.style.color = fg
    cell.style.border = `1px solid ${isDark ? "#111827" : "#ffffff"}`
    cell.style.padding = "2px 4px"
    cell.style.fontSize = "10px"
    cell.dataset.index = String(index)
    cell.dataset.action = "click->event-schema-treemap#onCellClick mouseenter->event-schema-treemap#onCellMouseEnter mouseleave->event-schema-treemap#onCellMouseLeave keydown->event-schema-treemap#onCellKeyDown"
    // Keyboard affordances — treemap cells act as buttons.
    cell.setAttribute("role", "button")
    cell.setAttribute("tabindex", "0")
    cell.setAttribute("aria-label", `${item.name}, ${this._typeLabel(item.type, " or ")}, shared by ${this._countOf(item.overlap, "event type")}. Enter to open, Shift+Enter to select.`)
    this._setSelected(cell, this.selected.has(index))

    // Tiered rendering by cell size; tooltip covers whatever doesn't fit.
    if (rect.w >= 18 && rect.h >= 8) {
      const name = document.createElement("div")
      name.className = "font-mono truncate"
      name.style.fontSize = rect.h >= 20 ? "10px" : "9px"
      name.textContent = item.name
      cell.appendChild(name)

      if (rect.h >= 28 && rect.w >= 44) {
        const meta = document.createElement("div")
        meta.className = "flex items-center gap-1 mt-1 truncate"
        meta.appendChild(this._typePill(item.type, isDark))
        if (this._isNullable(item.type)) meta.appendChild(this._nullablePill())
        const overlap = document.createElement("span")
        overlap.className = "opacity-80 truncate"
        overlap.style.fontSize = "9px"
        overlap.textContent = `• ${item.overlap}`
        meta.appendChild(overlap)
        cell.appendChild(meta)
      }
    }
    return cell
  }

  _mixProductColor(sources, isDark) {
    const rgbFor = (p) => isDark ? PRODUCT_RGB[p]?.dark : PRODUCT_RGB[p]?.light
    const result = (rgb, breakdown = "") =>
      ({ bg: `rgb(${rgb.join(", ")})`, fg: this._readableFg(rgb), breakdown })

    // Count sources per recognized product; unknown products are dropped.
    const counts = new Map()
    for (const s of sources || []) {
      const p = (s.product || "").toLowerCase()
      if (PRODUCT_RGB[p]) counts.set(p, (counts.get(p) || 0) + 1)
    }
    if (counts.size === 0) {
      return result(isDark ? UNKNOWN_PRODUCT_RGB.dark : UNKNOWN_PRODUCT_RGB.light)
    }

    // Weighted RGB average of contributing products.
    const mix = [0, 0, 0]
    let weight = 0
    for (const [p, n] of counts) {
      const [r, g, b] = rgbFor(p)
      mix[0] += r * n; mix[1] += g * n; mix[2] += b * n; weight += n
    }
    const breakdown = [...counts.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([p, n]) => `${p.toUpperCase()}: ${n}`).join(", ")
    return result(mix.map(v => Math.round(v / weight)), breakdown)
  }

  _scheduleSearch() {
    // 150ms debounce: wait for the user to pause before touching the DOM.
    if (this._searchDebounce) clearTimeout(this._searchDebounce)
    this._searchDebounce = setTimeout(() => { this._searchDebounce = null; this._applySearch() }, 150)
  }

  _applySearch() {
    const query = (this._searchInput?.value || "").trim().toLowerCase()
    this._searchQuery = query
    this.element.querySelectorAll("[data-index]").forEach(cell => {
      const item = this._cellItem(cell)
      if (!item) return
      const matches = !query || item.name.toLowerCase().includes(query)
      cell.style.opacity = matches ? "" : "0.1"
    })
  }

  _renderLegend() {
    const canvas = document.getElementById("event-shared-treemap-legend-canvas")
    if (!canvas) return
    const ctx = canvas.getContext("2d")
    if (!ctx) return
    const w = canvas.width
    const h = canvas.height
    const isDark = this._isDark()

    // Equilateral triangle centered with a small margin for line caps.
    const margin = 3
    const side = Math.min(w - 2 * margin, (h - 2 * margin) * 2 / Math.sqrt(3))
    const triHeight = side * Math.sqrt(3) / 2
    const cx = w / 2
    const topY = (h - triHeight) / 2
    const vTop = { x: cx, y: topY }
    const vLeft = { x: cx - side / 2, y: topY + triHeight }
    const vRight = { x: cx + side / 2, y: topY + triHeight }
    const shade = p => isDark ? PRODUCT_RGB[p].dark : PRODUCT_RGB[p].light
    const cTop = shade("edr"), cLeft = shade("itdr"), cRight = shade("siem")

    // 2x2 supersampling gives antialiased edges via fractional alpha at the boundary.
    const img = ctx.createImageData(w, h)
    const denom = (vLeft.y - vRight.y) * (vTop.x - vRight.x) + (vRight.x - vLeft.x) * (vTop.y - vRight.y)
    const SAMPLES = 2
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        let rSum = 0, gSum = 0, bSum = 0, coverage = 0
        for (let sy = 0; sy < SAMPLES; sy++) {
          for (let sx = 0; sx < SAMPLES; sx++) {
            const px = x + (sx + 0.5) / SAMPLES
            const py = y + (sy + 0.5) / SAMPLES
            const a = ((vLeft.y - vRight.y) * (px - vRight.x) + (vRight.x - vLeft.x) * (py - vRight.y)) / denom
            const b = ((vRight.y - vTop.y) * (px - vRight.x) + (vTop.x - vRight.x) * (py - vRight.y)) / denom
            const c = 1 - a - b
            if (a >= 0 && b >= 0 && c >= 0) {
              rSum += a * cTop[0] + b * cLeft[0] + c * cRight[0]
              gSum += a * cTop[1] + b * cLeft[1] + c * cRight[1]
              bSum += a * cTop[2] + b * cLeft[2] + c * cRight[2]
              coverage++
            }
          }
        }
        const i = (y * w + x) * 4
        const total = SAMPLES * SAMPLES
        if (coverage > 0) {
          img.data[i]     = Math.round(rSum / coverage)
          img.data[i + 1] = Math.round(gSum / coverage)
          img.data[i + 2] = Math.round(bSum / coverage)
          img.data[i + 3] = Math.round((coverage / total) * 255)
        } else {
          img.data[i + 3] = 0
        }
      }
    }
    ctx.putImageData(img, 0, 0)
  }

  // Rec. 601 perceived luminance — picks white or near-black for contrast.
  _readableFg([r, g, b]) {
    const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255
    return lum > 0.6 ? "#111827" : "#ffffff"
  }

  // Nullable JSON Schema unions (["string", "null"]) render as primary type +
  // separate nullable badge, not one combined pill.
  _primaryType(type) {
    return Array.isArray(type) ? (type.find(t => t !== "null") || type[0]) : type
  }

  _isNullable(type) {
    return Array.isArray(type) && type.includes("null")
  }

  _typePill(type, isDark) {
    const typeKey = this._primaryType(type)
    const [light, border, dark] = TYPE_PALETTE[typeKey] || DEFAULT_TYPE_PALETTE
    const pill = document.createElement("span")
    pill.className = `${PILL_BASE} font-semibold`
    pill.style.background = isDark ? dark : light
    pill.style.color = isDark ? light : dark
    pill.style.border = `1px solid ${border}`
    pill.textContent = String(typeKey ?? "")
    return pill
  }

  _nullablePill() {
    const pill = document.createElement("span")
    pill.className = NULLABLE_PILL_CLASSES
    pill.textContent = "nullable"
    return pill
  }

  _esc(str) {
    const d = document.createElement("div")
    d.textContent = String(str ?? "")
    return d.innerHTML
  }
}

// Squarified treemap layout (Bruls, Huijnen, van Wijk 2000). Inputs: areas
// pre-scaled to container w*h and the container rect. Output: a rect per
// input in the same order.
function squarify(values, rect) {
  const result = new Array(values.length)
  let row = []
  let rowIndices = []
  let remaining = { ...rect }
  let i = 0

  const placeRow = () => {
    const shortSide = Math.min(remaining.w, remaining.h)
    const sum = row.reduce((a, b) => a + b, 0)
    const thickness = sum / shortSide
    if (remaining.w >= remaining.h) {
      // Stack cells vertically in a column of width=thickness.
      let y = remaining.y
      for (let k = 0; k < row.length; k++) {
        const height = row[k] / thickness
        result[rowIndices[k]] = { x: remaining.x, y, w: thickness, h: height }
        y += height
      }
      remaining = { x: remaining.x + thickness, y: remaining.y, w: remaining.w - thickness, h: remaining.h }
    } else {
      // Lay cells horizontally in a row of height=thickness.
      let x = remaining.x
      for (let k = 0; k < row.length; k++) {
        const width = row[k] / thickness
        result[rowIndices[k]] = { x, y: remaining.y, w: width, h: thickness }
        x += width
      }
      remaining = { x: remaining.x, y: remaining.y + thickness, w: remaining.w, h: remaining.h - thickness }
    }
    row = []
    rowIndices = []
  }

  while (i < values.length) {
    if (remaining.w <= 0 || remaining.h <= 0) break
    const next = values[i]
    const shortSide = Math.min(remaining.w, remaining.h)
    if (row.length === 0 || worst([...row, next], shortSide) <= worst(row, shortSide)) {
      row.push(next)
      rowIndices.push(i)
      i++
    } else {
      placeRow()
    }
  }
  if (row.length > 0) placeRow()
  // Items that fell off the end (remaining collapsed) get zero-size rects.
  for (let k = 0; k < result.length; k++) if (!result[k]) result[k] = { x: 0, y: 0, w: 0, h: 0 }
  return result
}

function worst(row, shortSide) {
  const sum = row.reduce((a, b) => a + b, 0)
  const max = Math.max(...row)
  const min = Math.min(...row)
  const s2 = sum * sum
  const r2 = shortSide * shortSide
  return Math.max((r2 * max) / s2, s2 / (r2 * min))
}
